import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Mountain, MapPin, Users, Star, ArrowLeft, Heart, Share, 
  Check, Calendar, ChevronLeft, ChevronRight, Wifi, Car, 
  Flame, Tv, Wind, Bath, Coffee, UtensilsCrossed, Trees,
  Loader2, Phone, MessageCircle, X, Shield, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AMENITY_ICONS = {
  'wifi': Wifi,
  'parking': Car,
  'hot water': Flame,
  'tv': Tv,
  'heating': Wind,
  'kitchen': UtensilsCrossed,
  'garden': Trees,
  'balcony': Trees,
  'mountain view': Mountain,
  'river view': Trees,
  'bonfire': Flame
};

const StayDetail = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Booking form state
  const [bookingData, setBookingData] = useState({
    check_in: '',
    check_out: '',
    guests: 2,
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    special_requests: ''
  });

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stays/${listingId}`);
      if (response.ok) {
        const data = await response.json();
        setListing(data);
      } else {
        toast.error('Listing not found');
        navigate('/stays');
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Failed to load listing');
    }
    setLoading(false);
  };

  const calculateNights = () => {
    if (!bookingData.check_in || !bookingData.check_out) return 0;
    const checkIn = new Date(bookingData.check_in);
    const checkOut = new Date(bookingData.check_out);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    return nights * (listing?.price_per_night || 0);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingData.check_in || !bookingData.check_out) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    if (!bookingData.guest_name || !bookingData.guest_phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }

    const nights = calculateNights();
    if (nights < 1) {
      toast.error('Check-out must be after check-in');
      return;
    }

    setBookingLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/bookings/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listingId,
          listing_type: 'stay',
          ...bookingData
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Booking request submitted! The host will respond within 24 hours.');
        setShowBookingModal(false);
        // Could redirect to a booking confirmation page
        navigate(`/booking/confirmation?id=${data.booking_id}`);
      } else {
        toast.error(data.detail || 'Booking failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }

    setBookingLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C5F8D]" />
      </div>
    );
  }

  if (!listing) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" />
              <span className="text-lg font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Share className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Heart className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Image Gallery */}
      <div className="relative">
        <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-gray-100">
          {listing.images?.length > 0 ? (
            <img 
              src={listing.images[activeImage]} 
              alt={listing.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Mountain className="w-20 h-20 text-gray-300" />
            </div>
          )}
        </div>

        {listing.images?.length > 1 && (
          <>
            <button 
              onClick={() => setActiveImage(prev => prev > 0 ? prev - 1 : listing.images.length - 1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setActiveImage(prev => prev < listing.images.length - 1 ? prev + 1 : 0)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {listing.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`w-2 h-2 rounded-full ${idx === activeImage ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Title & Location */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-[#2C5F8D] mb-2">
                <span className="px-2 py-0.5 bg-blue-50 rounded">{listing.property_type}</span>
                {listing.rating > 0 && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {listing.rating.toFixed(1)} ({listing.review_count || 0} reviews)
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{listing.title}</h1>
              <p className="text-gray-500 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {listing.location?.address && `${listing.location.address}, `}
                {listing.location?.city}, {listing.location?.state}
              </p>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-4 py-4 border-y mb-6">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-400" />
                <span>{listing.max_guests} guests</span>
              </div>
              <div className="flex items-center gap-2">
                <span>{listing.bedrooms} bedrooms</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-gray-400" />
                <span>{listing.bathrooms} bathrooms</span>
              </div>
            </div>

            {/* Host Info */}
            {listing.host && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                <div className="w-14 h-14 rounded-full bg-[#2C5F8D] flex items-center justify-center text-white text-xl font-bold">
                  {listing.host.business_name?.charAt(0) || 'H'}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Hosted by {listing.host.business_name}</h3>
                  <p className="text-sm text-gray-500">
                    {listing.host.verified && (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <Shield className="w-4 h-4" /> Verified Host
                      </span>
                    )}
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  <MessageCircle className="w-4 h-4 mr-2" /> Contact
                </Button>
              </div>
            )}

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">About this place</h2>
              <p className="text-gray-600 whitespace-pre-line">{listing.description}</p>
            </div>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">What this place offers</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {listing.amenities.map((amenity, idx) => {
                    const IconComponent = AMENITY_ICONS[amenity.toLowerCase()] || Check;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <IconComponent className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-700">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* House Rules */}
            {listing.house_rules && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">House rules</h2>
                <div className="space-y-2 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    Check-in: {listing.check_in_time || '2:00 PM'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    Check-out: {listing.check_out_time || '11:00 AM'}
                  </div>
                  {listing.house_rules && (
                    <p className="mt-2">{listing.house_rules}</p>
                  )}
                </div>
              </div>
            )}

            {/* Cancellation Policy */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Cancellation policy</h2>
              <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Host-Friendly Policy</p>
                    <p>{listing.cancellation_policy?.description || 
                      'Free cancellation up to 7 days before check-in. 50% refund for cancellations 3-7 days before. No refund for last-minute cancellations.'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            {listing.reviews?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Guest reviews</h2>
                <div className="space-y-4">
                  {listing.reviews.map((review, idx) => (
                    <div key={idx} className="p-4 border rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{review.guest_name}</div>
                          <div className="text-sm text-gray-500">{new Date(review.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="ml-auto flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          {review.rating}
                        </div>
                      </div>
                      <p className="text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 p-6 border rounded-2xl shadow-lg bg-white">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-2xl font-bold text-gray-900">
                  ₹{listing.price_per_night?.toLocaleString()}
                </span>
                <span className="text-gray-500">/ night</span>
              </div>

              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Check-in</label>
                    <Input
                      type="date"
                      value={bookingData.check_in}
                      onChange={(e) => setBookingData(prev => ({ ...prev, check_in: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Check-out</label>
                    <Input
                      type="date"
                      value={bookingData.check_out}
                      onChange={(e) => setBookingData(prev => ({ ...prev, check_out: e.target.value }))}
                      min={bookingData.check_in || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Guests</label>
                  <Input
                    type="number"
                    value={bookingData.guests}
                    onChange={(e) => setBookingData(prev => ({ ...prev, guests: parseInt(e.target.value) || 1 }))}
                    min="1"
                    max={listing.max_guests}
                  />
                </div>
              </div>

              {calculateNights() > 0 && (
                <div className="space-y-2 py-4 border-t border-b mb-4">
                  <div className="flex justify-between text-gray-600">
                    <span>₹{listing.price_per_night?.toLocaleString()} x {calculateNights()} nights</span>
                    <span>₹{calculateTotal().toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span>₹{calculateTotal().toLocaleString()}</span>
                  </div>
                </div>
              )}

              <Button 
                className="w-full h-12 bg-[#E67E22] hover:bg-[#d35400] text-lg"
                onClick={() => setShowBookingModal(true)}
                disabled={calculateNights() < 1}
              >
                Request to Book
              </Button>

              <p className="text-center text-sm text-gray-500 mt-3">
                You won't be charged yet
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Complete your booking</h2>
              <button onClick={() => setShowBookingModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBookingSubmit} className="p-6">
              {/* Booking Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200">
                    {listing.images?.[0] && (
                      <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{listing.title}</h3>
                    <p className="text-sm text-gray-500">
                      {bookingData.check_in} → {bookingData.check_out}
                    </p>
                    <p className="text-sm text-gray-500">{bookingData.guests} guests</p>
                  </div>
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t font-semibold">
                  <span>Total ({calculateNights()} nights)</span>
                  <span>₹{calculateTotal().toLocaleString()}</span>
                </div>
              </div>

              {/* Guest Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <Input
                    value={bookingData.guest_name}
                    onChange={(e) => setBookingData(prev => ({ ...prev, guest_name: e.target.value }))}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <Input
                    value={bookingData.guest_phone}
                    onChange={(e) => setBookingData(prev => ({ ...prev, guest_phone: e.target.value }))}
                    placeholder="+91 9876543210"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                  <Input
                    type="email"
                    value={bookingData.guest_email}
                    onChange={(e) => setBookingData(prev => ({ ...prev, guest_email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                  <textarea
                    value={bookingData.special_requests}
                    onChange={(e) => setBookingData(prev => ({ ...prev, special_requests: e.target.value }))}
                    placeholder="Any special requirements..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2C5F8D] focus:outline-none"
                    rows={3}
                  />
                </div>
              </div>

              {/* Cancellation Notice */}
              <div className="mt-6 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <strong>Cancellation Policy:</strong> Free cancellation up to 7 days before check-in.
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 mt-6 bg-[#E67E22] hover:bg-[#d35400]"
                disabled={bookingLoading}
              >
                {bookingLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Submit Booking Request'
                )}
              </Button>

              <p className="text-center text-xs text-gray-500 mt-3">
                The host will review your request. Payment is required only after approval.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StayDetail;
